<script setup lang="ts">
import type { ChatProvider } from '@xsai-ext/shared-providers'

import { storeToRefs } from 'pinia'
import { onMounted, onUnmounted, ref, watch } from 'vue'

import { useCall } from '../../composables/useCall'
import { useCallStore } from '../../stores/call'
import { useChatStore } from '../../stores/chat'
import { applyCallModePrefix, createAssistantMessage } from '../../stores/chat-helpers'
import { useConsciousnessStore } from '../../stores/modules/consciousness'
import { useProvidersStore } from '../../stores/providers'
import { showWarning } from '../../utils/toast'

const callStore = useCallStore()
const chatStore = useChatStore()
const providersStore = useProvidersStore()
const { activeProvider, activeModel } = storeToRefs(useConsciousnessStore())
const { sending } = storeToRefs(chatStore)

const {
  sendCallMessage,
  endCall,
  currentTranscript,
  callCharacterName,
  callCharacterAvatar,
} = useCall()

const messageInput = ref('')
const transcriptContainer = ref<HTMLElement | null>(null)
const timerInterval = ref<number | null>(null)
const displayDuration = ref('00:00')
const isAISpeaking = ref(false)

// 长按重新生成
const longPressTimer = ref<number | null>(null)
const activeMessageIndex = ref<number | null>(null)
const showRegenMenu = ref(false)
const regenMenuPosition = ref({ x: 0, y: 0 })
const showRegenConfirm = ref(false)
const pendingRegenIndex = ref<number | null>(null)

// ⏱️ AI回复防抖定时器
let aiReplyDebounceTimer: number | null = null
const AI_REPLY_DEBOUNCE_DELAY = 3000 // 3秒
const hasUnrepliedMessages = ref(false)
const pendingAutoReply = ref(false)
const incomingGreetingSent = ref(false) // 防止来电问候语重复触发

// 更新通话时长显示
function updateDuration() {
  if (callStore.isCallActive && callStore.callStartTime) {
    const elapsed = Math.floor((Date.now() - callStore.callStartTime) / 1000)
    displayDuration.value = callStore.formatDuration(elapsed)
  }
}

// 启动计时器
function startTimer() {
  updateDuration()
  timerInterval.value = window.setInterval(updateDuration, 1000)
}

// 停止计时器
function stopTimer() {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
    timerInterval.value = null
  }
}

async function fireAutoReply() {
  // 检查是否已配置 provider 和 model
  if (!activeProvider.value || !activeModel.value) {
    console.warn('⏭️ 尚未配置服务商或模型，跳过通话AI回复')
    return
  }

  try {
    const providerConfig = providersStore.getProviderConfig(activeProvider.value)

    await chatStore.send('', {
      providerId: activeProvider.value,
      chatProvider: await providersStore.getProviderInstance(activeProvider.value) as ChatProvider,
      model: activeModel.value,
      providerConfig,
      skipUserMessage: true,
    })

    console.info('✅ 通话AI回复已触发')
  }
  catch (error) {
    console.error('AI回复失败:', error)

    // 检查是否为网络错误
    const errorMessage = (error as Error).message || String(error)
    if (errorMessage.includes('NetworkError') || errorMessage.includes('504') || errorMessage.includes('timeout')) {
      showWarning('通话连接不稳定，请稍后重试', 3000)
    }
  }
}

// ⏱️ 使用防抖机制触发AI回复
function triggerAIReplyWithDebounce() {
  // 清除之前的定时器
  if (aiReplyDebounceTimer !== null) {
    clearTimeout(aiReplyDebounceTimer)
  }

  // 设置新的定时器
  aiReplyDebounceTimer = window.setTimeout(async () => {
    console.info('⏱️ 通话防抖延迟结束，触发AI回复')
    aiReplyDebounceTimer = null

    if (sending.value) {
      console.info('📞 AI 正在回复，延后执行通话自动回复')
      pendingAutoReply.value = true
      return
    }

    await fireAutoReply()
  }, AI_REPLY_DEBOUNCE_DELAY)

  console.info(`⏱️ 已设置通话AI回复防抖定时器 (${AI_REPLY_DEBOUNCE_DELAY}ms)`)
}

// 🚫 取消AI回复防抖
function cancelAIReplyDebounce() {
  if (aiReplyDebounceTimer !== null) {
    clearTimeout(aiReplyDebounceTimer)
    aiReplyDebounceTimer = null
    console.info('🚫 已取消通话AI回复防抖定时器')
  }
}

// 长按相关
function startLongPress(event: PointerEvent, index: number) {
  if (sending.value)
    return
  if (currentTranscript.value[index]?.role !== 'assistant')
    return

  const { clientX, clientY } = event

  longPressTimer.value = window.setTimeout(() => {
    activeMessageIndex.value = index
    const target = event.target as HTMLElement
    const bubble = target.closest('.message-content')
    if (bubble) {
      const rect = bubble.getBoundingClientRect()
      const menuY = rect.top < 60 ? rect.bottom + 8 : rect.top
      regenMenuPosition.value = { x: rect.left + rect.width / 2, y: menuY }
    }
    else {
      regenMenuPosition.value = { x: clientX, y: clientY }
    }
    showRegenMenu.value = true
  }, 500)
}

function cancelLongPress() {
  if (longPressTimer.value) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
  }
}

function handleTouchMove() {
  cancelLongPress()
}

function handleRegenerate() {
  pendingRegenIndex.value = activeMessageIndex.value
  showRegenMenu.value = false
  showRegenConfirm.value = true
}

async function confirmRegenerate() {
  const index = pendingRegenIndex.value
  showRegenConfirm.value = false
  pendingRegenIndex.value = null
  activeMessageIndex.value = null

  if (index === null || index < 0)
    return

  const targetMessage = currentTranscript.value[index]
  if (!targetMessage)
    return

  cancelAIReplyDebounce()

  const targetTimestamp = targetMessage.timestamp
  callStore.truncateTranscriptFrom(index)

  chatStore.messages = chatStore.messages.filter((msg: any) => {
    if (msg.isHidden && msg.timestamp >= targetTimestamp)
      return false
    return true
  })

  await fireAutoReply()
}

function cancelRegenMenu() {
  showRegenMenu.value = false
  activeMessageIndex.value = null
}

function cancelRegenConfirm() {
  showRegenConfirm.value = false
  pendingRegenIndex.value = null
  activeMessageIndex.value = null
}

function scrollTranscriptToBottom() {
  setTimeout(() => {
    if (transcriptContainer.value)
      transcriptContainer.value.scrollTop = transcriptContainer.value.scrollHeight
  }, 50)
}

// 发送消息
async function handleSend() {
  if (!messageInput.value.trim())
    return

  // 💾 保存要发送的内容
  const textToSend = messageInput.value

  // ⚡ 立即清空输入框
  messageInput.value = ''

  // 🚫 取消防抖，避免重复触发 AI 回复
  cancelAIReplyDebounce()
  hasUnrepliedMessages.value = false

  try {
    // 如果 AI 正在回复，只添加用户消息，不触发新的 AI 回复
    // 等当前回复结束后通过 pendingAutoReply 机制触发
    if (sending.value) {
      console.info('📞 AI 正在回复，仅添加用户消息到队列')
      const callPrefix = callStore.callType === 'video' ? '视频通话' : '语音通话'
      callStore.addToTranscript({ role: 'user', content: textToSend, timestamp: Date.now() })
      chatStore.messages = [...chatStore.messages, {
        role: 'user',
        content: `[${callPrefix}]: ${textToSend}`,
        isHidden: true,
      } as any]
      pendingAutoReply.value = true
      scrollTranscriptToBottom()
      return
    }

    const providerConfig = providersStore.getProviderConfig(activeProvider.value)
    const sendOptions = {
      providerId: activeProvider.value,
      chatProvider: await providersStore.getProviderInstance(activeProvider.value) as ChatProvider,
      model: activeModel.value,
      providerConfig,
    }

    const success = await sendCallMessage(textToSend, sendOptions)
    if (success) {
      console.info('✅ 通话消息已发送，AI回复已由 sendCallMessage 触发')
      scrollTranscriptToBottom()
    }
    else {
      // 发送失败，恢复输入内容
      messageInput.value = textToSend
    }
  }
  catch (error) {
    console.error('发送消息失败:', error)
    // 发送失败，恢复输入内容
    messageInput.value = textToSend
  }
}

// 挂断通话
async function handleEndCall() {
  stopTimer()
  cancelAIReplyDebounce() // 清除防抖定时器
  try {
    await endCall()
  }
  catch (error) {
    console.error('📞 [CallModal] endCall 出错:', error)
    // 强制重置通话状态
    callStore.resetCallState()
  }
}

// 监听输入框变化，实现防抖逻辑
watch(messageInput, (newValue) => {
  // 当输入框为空且有未回复的消息时，启动防抖计时器
  if (!newValue.trim() && hasUnrepliedMessages.value) {
    console.info('⏱️ 通话输入框已清空，启动AI回复防抖计时器')
    triggerAIReplyWithDebounce()
  }
  // 当输入框有内容时，说明用户正在输入，取消AI回复
  else if (newValue.trim()) {
    console.info('⏱️ 用户正在输入，取消AI回复计时器')
    cancelAIReplyDebounce()
  }
})

// 监听AI回复开始，重置未回复消息标志
watch(isAISpeaking, (isSpeaking) => {
  if (isSpeaking) {
    hasUnrepliedMessages.value = false
    cancelAIReplyDebounce()
  }
})

watch(sending, (isSending) => {
  if (!isSending && pendingAutoReply.value) {
    console.info('▶️ 通话AI回复完成，执行待处理的自动回复')
    pendingAutoReply.value = false
    void fireAutoReply()
  }
})

// 监听通话状态变化
watch(() => callStore.isCallActive, (isActive) => {
  if (isActive) {
    startTimer()

    // 来电接听后，触发 AI 说第一句话（仅触发一次）
    // callInitiator === 'ai' 表示这是 AI 发起的来电，用户刚接听
    // 呼出通话的问候语已在 handleCallDecision 中处理，无需重复触发
    if (callStore.callInitiator === 'ai' && !incomingGreetingSent.value) {
      incomingGreetingSent.value = true

      // 优先使用AI发起通话时附带的问候语，避免二次AI请求
      if (callStore.pendingGreeting) {
        const greeting = callStore.pendingGreeting
        callStore.pendingGreeting = null
        console.info('📞 来电已接通，使用预存问候语:', greeting.substring(0, 50))
        callStore.addToTranscript({
          role: 'assistant',
          content: greeting,
          timestamp: Date.now(),
        })
        // 同时加入 chatStore.messages（标记为隐藏），让 AI 知道自己已打过招呼
        const greetingMsg = createAssistantMessage(greeting)
        applyCallModePrefix(greetingMsg, callStore.callType)
        chatStore.messages = [...chatStore.messages, greetingMsg]
        void chatStore.triggerFullTextSpeech(greeting)
      }
      else {
        // 兜底：AI未提供问候语时，发送请求获取
        console.info('📞 来电已接通，AI未提供问候语，触发fireAutoReply')
        void fireAutoReply()
      }
    }
  }
  else {
    stopTimer()
    displayDuration.value = '00:00'
    cancelAIReplyDebounce() // 通话结束时清除防抖定时器
    hasUnrepliedMessages.value = false
    incomingGreetingSent.value = false // 通话结束后重置标志
  }
})

// 自动滚动到最新消息
watch(() => currentTranscript.value.length, () => {
  scrollTranscriptToBottom()
})

// 监听最后一条消息变化，显示"正在说话..."
watch(() => currentTranscript.value, (newTranscript) => {
  if (newTranscript.length > 0) {
    const lastMsg = newTranscript[newTranscript.length - 1]
    if (lastMsg.role === 'assistant') {
      isAISpeaking.value = true
      setTimeout(() => {
        isAISpeaking.value = false
      }, 2000)
    }
  }
}, { deep: true })

onMounted(() => {
  if (callStore.isCallActive)
    startTimer()
})

onUnmounted(() => {
  stopTimer()
  cancelAIReplyDebounce()
  cancelLongPress()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="callStore.isCallActive"
        fixed inset-0 z-200
        class="call-modal-backdrop"
      >
        <!-- 顶部装饰条 -->
        <div class="top-decoration" />

        <!-- 主要内容 -->
        <div flex="~ col" h-full w-full>
          <!-- 头部区域 -->
          <div class="call-header" flex="~ col" items-center pb-6 pt-16>
            <!-- 头像区域 -->
            <div class="avatar-section" relative mb-6>
              <!-- 外层光晕 -->
              <div class="avatar-glow" />

              <!-- 头像 -->
              <div class="avatar-container" relative z-10>
                <img
                  :src="callCharacterAvatar || '/default-avatar.png'"
                  :alt="callCharacterName || 'Character'"
                  class="avatar-image"
                >

                <!-- 说话时的波纹 -->
                <div v-if="isAISpeaking" class="avatar-ripple" />
                <div v-if="isAISpeaking" class="avatar-ripple ripple-2" />
              </div>
            </div>

            <!-- 名字 -->
            <h2 class="character-name" mb-3>
              {{ callCharacterName || '未知' }}
            </h2>

            <!-- 通话时长 -->
            <div class="call-duration" mb-3 flex items-center gap-2>
              <div i-solar:clock-circle-bold-duotone text-lg />
              <span class="duration-text">{{ displayDuration }}</span>
            </div>

            <!-- 状态提示 -->
            <Transition name="status">
              <div v-if="isAISpeaking" class="status-badge">
                <div class="status-dot" />
                <span>正在说话...</span>
              </div>
            </Transition>
          </div>

          <!-- 对话区域 -->
          <div class="chat-section" flex-1 overflow-hidden>
            <div
              ref="transcriptContainer"
              class="messages-container"
              h-full overflow-y-auto px-6
            >
              <!-- 空状态 -->
              <div
                v-if="currentTranscript.length === 0"
                class="empty-state"
                h-full flex="~ col" items-center justify-center
              >
                <div class="empty-icon" mb-4>
                  <div i-solar:chat-dots-bold-duotone />
                </div>
                <p class="empty-text">
                  开始你们的对话吧
                </p>
              </div>

              <!-- 消息列表 -->
              <div v-else class="messages-list" py-6 space-y-4>
                <div
                  v-for="(msg, i) in currentTranscript"
                  :key="i"
                  class="message-row"
                  :class="msg.role === 'user' ? 'message-row-user' : 'message-row-assistant'"
                >
                  <div
                    class="message-content"
                    @pointerdown="(e: PointerEvent) => startLongPress(e, i)"
                    @pointerup="cancelLongPress"
                    @pointercancel="cancelLongPress"
                    @touchmove="handleTouchMove"
                    @contextmenu.prevent
                  >
                    {{ msg.content }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 底部控制区 -->
          <div class="call-footer" px-6 pb-8>
            <div class="control-container" mx-auto max-w-3xl>
              <!-- 输入区域 -->
              <div class="input-section" mb-5>
                <form flex items-center gap-3 @submit.prevent="handleSend">
                  <!-- 输入框 -->
                  <div class="input-wrapper" flex-1>
                    <input
                      v-model="messageInput"
                      type="text"
                      class="message-input"
                      placeholder="输入消息..."
                    >
                  </div>

                  <!-- 发送按钮 -->
                  <button
                    type="submit"
                    class="tool-button send-button"
                    :disabled="!messageInput.trim()"
                  >
                    <div i-solar:plain-2-bold-duotone />
                  </button>
                </form>
              </div>

              <!-- 挂断按钮 -->
              <div flex justify-center>
                <button class="hangup-button" @click="handleEndCall">
                  <div class="hangup-icon">
                    <div i-solar:phone-calling-rounded-bold />
                  </div>
                  <span class="hangup-text">结束通话</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 重新生成菜单 -->
        <Transition name="fade">
          <div v-if="showRegenMenu" fixed inset-0 z-210 @click="cancelRegenMenu">
            <div
              class="regen-menu"
              :style="{ left: `${regenMenuPosition.x}px`, top: `${regenMenuPosition.y - 8}px` }"
              @click.stop
            >
              <button class="regen-menu-item" @click="handleRegenerate">
                <div i-solar:refresh-bold-duotone text-base />
                <span>重新生成</span>
              </button>
            </div>
          </div>
        </Transition>

        <!-- 重新生成确认框 -->
        <Transition name="fade">
          <div v-if="showRegenConfirm" fixed inset-0 z-220 flex items-center justify-center @click="cancelRegenConfirm">
            <div class="regen-confirm" @click.stop>
              <p class="regen-confirm-text">
                确定重新生成这条消息吗？该消息及之后的所有消息将被删除。
              </p>
              <div class="regen-confirm-actions">
                <button class="regen-btn regen-btn-cancel" @click="cancelRegenConfirm">
                  取消
                </button>
                <button class="regen-btn regen-btn-confirm" @click="confirmRegenerate">
                  确定
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* ==================== 背景 ==================== */
.call-modal-backdrop {
 background:
 radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
 radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.15) 0%, transparent 50%),
 radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
 linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
 backdrop-filter: blur(100px) saturate(150%);
 -webkit-backdrop-filter: blur(100px) saturate(150%);
}

/* 顶部装饰条 */
.top-decoration {
 position: absolute;
 top: 0;
 left: 0;
 right: 0;
 height: 1px;
 background: linear-gradient(90deg,
 transparent 0%,
 rgba(255, 255, 255, 0.3) 50%,
 transparent 100%
 );
}

/* ==================== 头部区域 ==================== */
.call-header {
 position: relative;
 z-index: 10;
}

/* 头像区域 */
.avatar-section {
 width: 160px;
 height: 160px;
 display: flex;
 align-items: center;
 justify-content: center;
}

.avatar-glow {
 position: absolute;
 inset: -20px;
 background: radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%);
 border-radius: 50%;
 filter: blur(30px);
 animation: glowPulse 3s ease-in-out infinite;
}

@keyframes glowPulse {
 0%, 100% {
 opacity: 0.5;
 transform: scale(1);
 }
 50% {
 opacity: 1;
 transform: scale(1.1);
 }
}

.avatar-container {
 width: 140px;
 height: 140px;
 border-radius: 50%;
 padding: 4px;
 background: linear-gradient(135deg, rgba(99, 102, 241, 0.4), rgba(236, 72, 153, 0.4));
 box-shadow:
 0 20px 60px rgba(0, 0, 0, 0.4),
 inset 0 0 0 2px rgba(255, 255, 255, 0.1);
}

.avatar-image {
 width: 100%;
 height: 100%;
 border-radius: 50%;
 object-fit: cover;
 border: 3px solid rgba(15, 23, 42, 0.8);
}

.avatar-ripple {
 position: absolute;
 inset: -8px;
 border-radius: 50%;
 border: 2px solid rgb(99, 102, 241);
 opacity: 0;
 animation: ripple 2s ease-out infinite;
}

.ripple-2 {
 animation-delay: 1s;
}

@keyframes ripple {
 0% {
 transform: scale(1);
 opacity: 0.8;
 }
 100% {
 transform: scale(1.4);
 opacity: 0;
 }
}

/* 名字 */
.character-name {
 color: white;
 font-size: 2rem;
 letter-spacing: 0.02em;
 text-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
}

/* 通话时长 */
.call-duration {
 color: rgba(255, 255, 255, 0.7);
}

.duration-text {
 font-family: ui-monospace, monospace;
 font-size: 1.125rem;
 letter-spacing: 0.1em;
}

/* 状态徽章 */
.status-badge {
 display: inline-flex;
 align-items: center;
 gap: 0.5rem;
 padding: 0.5rem 1rem;
 background: rgba(99, 102, 241, 0.2);
 backdrop-filter: blur(10px);
 border: 1px solid rgba(99, 102, 241, 0.3);
 border-radius: 9999px;
 color: rgb(165, 180, 252);
 font-size: 0.875rem;
}

.status-dot {
 width: 8px;
 height: 8px;
 background: rgb(99, 102, 241);
 border-radius: 50%;
 box-shadow: 0 0 12px rgba(99, 102, 241, 0.8);
 animation: dotPulse 1.5s ease-in-out infinite;
}

@keyframes dotPulse {
 0%, 100% {
 opacity: 1;
 transform: scale(1);
 }
 50% {
 opacity: 0.5;
 transform: scale(1.2);
 }
}

.status-enter-active,
.status-leave-active {
 transition: all 0.3s ease;
}

.status-enter-from,
.status-leave-to {
 opacity: 0;
 transform: scale(0.8) translateY(-10px);
}

/* ==================== 对话区域 ==================== */
.chat-section {
 position: relative;
}

.messages-container {
 scrollbar-width: thin;
 scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.messages-container::-webkit-scrollbar {
 width: 6px;
}

.messages-container::-webkit-scrollbar-track {
 background: transparent;
}

.messages-container::-webkit-scrollbar-thumb {
 background: rgba(255, 255, 255, 0.1);
 border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
 background: rgba(255, 255, 255, 0.2);
}

/* 空状态 */
.empty-icon {
 font-size: 5rem;
 color: rgba(255, 255, 255, 0.1);
 filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
}

.empty-text {
 color: rgba(255, 255, 255, 0.3);
 font-size: 1rem;
}

/* 消息列表 */
.messages-list {
 max-width: 48rem;
 margin: 0 auto;
}

.message-row {
 display: flex;
 animation: messageIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.message-row-user {
 justify-content: flex-end;
}

.message-row-assistant {
 justify-content: flex-start;
}

@keyframes messageIn {
 from {
 opacity: 0;
 transform: translateY(20px) scale(0.9);
 }
 to {
 opacity: 1;
 transform: translateY(0) scale(1);
 }
}

.message-content {
 max-width: 75%;
 padding: 0.875rem 1.25rem;
 border-radius: 1.25rem;
 font-size: 0.9375rem;
 line-height: 1.6;
 word-wrap: break-word;
 transition: all 0.2s ease;
}

.message-row-user .message-content {
 background: linear-gradient(135deg, rgb(99, 102, 241) 0%, rgb(79, 70, 229) 100%);
 color: white;
 box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
 border-bottom-right-radius: 0.25rem;
}

.message-row-assistant .message-content {
 background: rgba(255, 255, 255, 0.08);
 backdrop-filter: blur(12px);
 color: rgba(255, 255, 255, 0.95);
 border: 1px solid rgba(255, 255, 255, 0.1);
 box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
 border-bottom-left-radius: 0.25rem;
}

.message-content:hover {
 transform: translateY(-2px);
 box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

/* ==================== 底部控制区 ==================== */
.call-footer {
 position: relative;
 background: linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.8) 100%);
 backdrop-filter: blur(20px);
 z-index: 10;
}

/* 输入区域 */
.input-wrapper {
 position: relative;
}

.message-input {
 width: 100%;
 padding: 0.875rem 1.25rem;
 background: rgba(255, 255, 255, 0.08);
 backdrop-filter: blur(12px);
 border: 1px solid rgba(255, 255, 255, 0.12);
 border-radius: 9999px;
 color: white;
 font-size: 0.9375rem;
 outline: none;
 transition: all 0.2s ease;
}

.message-input::placeholder {
 color: rgba(255, 255, 255, 0.4);
}

.message-input:focus {
 background: rgba(255, 255, 255, 0.12);
 border-color: rgba(99, 102, 241, 0.5);
 box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1), 0 4px 16px rgba(0, 0, 0, 0.2);
}

/* 工具按钮 */
.tool-button {
 width: 3rem;
 height: 3rem;
 display: flex;
 align-items: center;
 justify-content: center;
 border-radius: 50%;
 font-size: 1.5rem;
 transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
 border: 1px solid transparent;
}

.tool-button:active {
 transform: scale(0.9);
}

.send-button {
 background: linear-gradient(135deg, rgb(99, 102, 241), rgb(79, 70, 229));
 color: white;
 box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

.send-button:hover:not(:disabled) {
 transform: scale(1.08);
 box-shadow: 0 6px 24px rgba(99, 102, 241, 0.5);
}

.send-button:disabled {
 opacity: 0.4;
 cursor: not-allowed;
}

/* 挂断按钮 */
.hangup-button {
 display: inline-flex;
 align-items: center;
 gap: 0.75rem;
 padding: 0.875rem 2rem;
 background: linear-gradient(135deg, rgb(239, 68, 68), rgb(220, 38, 38));
 border-radius: 9999px;
 color: white;
 font-size: 0.9375rem;
 letter-spacing: 0.02em;
 box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
 transition: all 0.3s ease;
}

.hangup-icon {
 font-size: 1.25rem;
 transition: transform 0.3s ease;
}

.hangup-button:hover {
 transform: translateY(-2px) scale(1.02);
 box-shadow: 0 12px 32px rgba(239, 68, 68, 0.6);
 background: linear-gradient(135deg, rgb(220, 38, 38), rgb(185, 28, 28));
}

.hangup-button:hover .hangup-icon {
 transform: rotate(-15deg);
}

.hangup-button:active {
 transform: translateY(0) scale(0.98);
}

/* ==================== 过渡动画 ==================== */
.fade-enter-active,
.fade-leave-active {
 transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 离开过渡期间禁用指针事件，防止误触 */
.fade-leave-active {
 pointer-events: none !important;
}

.fade-enter-from {
 opacity: 0;
 transform: scale(0.96);
}

.fade-leave-to {
 opacity: 0;
 transform: scale(1.02);
}

/* ==================== 重新生成菜单 ==================== */
.regen-menu {
 position: fixed;
 transform: translate(-50%, -100%);
 background: rgba(30, 41, 59, 0.95);
 backdrop-filter: blur(16px);
 border: 1px solid rgba(255, 255, 255, 0.15);
 border-radius: 12px;
 padding: 4px;
 box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.regen-menu-item {
 display: flex;
 align-items: center;
 gap: 8px;
 padding: 10px 16px;
 border-radius: 8px;
 color: rgba(255, 255, 255, 0.9);
 font-size: 0.875rem;
 background: none;
 border: none;
 cursor: pointer;
 white-space: nowrap;
 transition: background 0.15s ease;
}

.regen-menu-item:hover {
 background: rgba(255, 255, 255, 0.1);
}

.regen-menu-item:active {
 background: rgba(255, 255, 255, 0.15);
}

/* ==================== 重新生成确认框 ==================== */
.regen-confirm {
 background: rgba(30, 41, 59, 0.95);
 backdrop-filter: blur(20px);
 border: 1px solid rgba(255, 255, 255, 0.12);
 border-radius: 16px;
 padding: 20px 24px;
 max-width: 300px;
 box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
}

.regen-confirm-text {
 color: rgba(255, 255, 255, 0.85);
 font-size: 0.875rem;
 line-height: 1.6;
 margin-bottom: 16px;
 text-align: center;
}

.regen-confirm-actions {
 display: flex;
 gap: 10px;
}

.regen-btn {
 flex: 1;
 padding: 8px 0;
 border-radius: 10px;
 font-size: 0.875rem;
 border: none;
 cursor: pointer;
 transition: all 0.15s ease;
}

.regen-btn-cancel {
 background: rgba(255, 255, 255, 0.1);
 color: rgba(255, 255, 255, 0.7);
}

.regen-btn-cancel:hover {
 background: rgba(255, 255, 255, 0.15);
}

.regen-btn-confirm {
 background: linear-gradient(135deg, rgb(99, 102, 241), rgb(79, 70, 229));
 color: white;
 box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.regen-btn-confirm:hover {
 box-shadow: 0 6px 16px rgba(99, 102, 241, 0.5);
}
</style>
